import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconPlus from '@apollo/icons/default/IconPlus.svg';
import IconTrash from '@apollo/icons/default/IconTrash.svg';
import {
  Badge,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
} from '@apollo/orbit';
import { Switch } from '@apollo/space-kit/Switch';
import classnames from 'classnames';
import { GraphQLSchema } from 'graphql';
import produce from 'immer';
import * as jsoncParser from 'jsonc-parser';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import { useInlineClassRangesEffect } from 'src/app/graph/explorerPage/hooks/useInlineClassRangesEffect';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { SchemaInfo } from 'src/components/editor/useApplyJSONSchema';
import { StudioEditorContainer } from 'src/hooks/useStudioEditor';
import { format } from 'src/lib/format';

import { PublicVariantWarning } from '../../../../components/PublicVariantWarning';
import { ExplorerBehavioralFlags } from '../explorerBehavioralFlags';
import { getCurrentDocsContext } from '../explorerDocs/getFieldToggle/getCurrentDocsContext';
import { useEditorModel } from '../hooks/useEditorModel';
import {
  EditorTabState,
  FileVariable,
} from '../hooks/useExplorerState/useEditorTabState/useEditorTabState';

import { computeJsonSchema } from './computeJsonSchema';

interface VariablesEditorProps {
  className?: string;
  schema: GraphQLSchema;
  operationEditor: monacoEditor.editor.IStandaloneCodeEditor;
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor;
  settingsEndpointUrl: string | null;
  settingsSubscriptionUrl: string | null;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  editorTabState: EditorTabState;
}

export const VariablesEditor = ({
  className,
  schema,
  operationEditor,
  variablesEditor,
  settingsEndpointUrl,
  settingsSubscriptionUrl,
  explorerBehavioralFlags,
  editorTabState,
}: VariablesEditorProps) => {
  const [schemaInfo, setSchemaInfo] = React.useState<SchemaInfo | undefined>();

  const stableGetSchemaInfo = React.useCallback(
    (editorValue: string) => {
      if (!schema) {
        return undefined;
      }
      const jsonSchema = computeJsonSchema(schema, editorValue);
      return (
        jsonSchema && {
          id: 'variablesJson',
          schema: jsonSchema,
          language: 'json' as const,
        }
      );
    },
    [schema],
  );
  const newSchemaInfo = useDerivedEditorValue({
    editor: operationEditor,
    getValue: stableGetSchemaInfo,
    withPosition: false,
  });
  React.useEffect(() => {
    if (newSchemaInfo) setSchemaInfo(newSchemaInfo);
  }, [newSchemaInfo]);

  const variablesModel = useEditorModel(variablesEditor);
  useInlineClassRangesEffect({
    className: 'fs-exclude',
    editor: variablesEditor,
    ranges: useDerivedEditorValue({
      editor: variablesEditor,
      getValue: useCallback(
        (text: string) => {
          if (!variablesModel) return [];
          const parsedTree = jsoncParser.parseTree(text);
          return parsedTree?.children?.flatMap((child) => {
            const value = child.children?.[1];
            if (value) {
              return [
                monacoEditor.Range.fromPositions(
                  variablesModel.getPositionAt(value.offset),
                  variablesModel.getPositionAt(value.offset + value.length),
                ),
              ];
            }
            return [];
          });
        },
        [variablesModel],
      ),
      withPosition: false,
    }),
  });

  const { tabbedFileVariableValues, setFileVariableValuesForTab, selectedTab } =
    editorTabState;

  const currentFileVariableValues = useMemo(
    () =>
      selectedTab ? (tabbedFileVariableValues[selectedTab] ?? []) : undefined,
    [selectedTab, tabbedFileVariableValues],
  );

  const handleAddFileClick = useCallback(() => {
    if (!selectedTab) return;
    const newValue = (currentFileVariableValues ?? []).concat([
      { files: undefined, variableKey: '', isMultiFile: false },
    ]);
    setFileVariableValuesForTab(selectedTab, newValue);
  }, [selectedTab, setFileVariableValuesForTab, currentFileVariableValues]);

  const handleFileVariablesNameChange = useCallback(
    (
      newFileVariableName: string,
      fileVariable: FileVariable,
      index: number,
      passedInCurrentFileVariableValues: FileVariable[],
    ) => {
      if (!selectedTab) return;

      const modifiedFileVariablesValues = Object.assign(
        [...passedInCurrentFileVariableValues],
        {
          [index]: {
            ...fileVariable,
            variableKey: newFileVariableName,
          },
        },
      );
      setFileVariableValuesForTab(selectedTab, modifiedFileVariablesValues);
    },
    [selectedTab, setFileVariableValuesForTab],
  );

  const handleFileVariablesMultiFileSwitchChange = useCallback(
    (
      isMultiFileValue: boolean,
      fileVariable: FileVariable,
      index: number,
      passedInCurrentFileVariableValues: FileVariable[],
    ) => {
      if (!selectedTab) return;
      const modifiedFileVariablesValues = [
        ...passedInCurrentFileVariableValues,
      ];
      modifiedFileVariablesValues[index] = {
        ...fileVariable,
        isMultiFile: isMultiFileValue,
        // when going from multi to single file, remove if there are too many files
        files:
          fileVariable.files &&
          fileVariable.files.length > 1 &&
          !isMultiFileValue
            ? undefined
            : fileVariable.files,
      };

      setFileVariableValuesForTab(selectedTab, modifiedFileVariablesValues);
    },
    [selectedTab, setFileVariableValuesForTab],
  );

  const handleRemoveFileVariable = useCallback(
    (index: number) => {
      if (!currentFileVariableValues || !selectedTab) return;
      setFileVariableValuesForTab(
        selectedTab,
        produce(currentFileVariableValues, (draftFileVariables) => {
          draftFileVariables.splice(index, 1);
        }),
      );
    },
    [currentFileVariableValues, selectedTab, setFileVariableValuesForTab],
  );

  const onSelectFiles = useCallback(
    (
      { target }: { target: HTMLInputElement },
      fileVariable: FileVariable,
      index: number,
      passedInCurrentFileVariableValues: FileVariable[],
    ) => {
      const targetFiles = target.files;
      if (!selectedTab || !(target.validity.valid && targetFiles)) return;
      const modifiedFileVariablesValues = [
        ...passedInCurrentFileVariableValues,
      ];
      modifiedFileVariablesValues[index] = {
        ...fileVariable,
        files: targetFiles,
      };
      setFileVariableValuesForTab(selectedTab, modifiedFileVariablesValues);
    },
    [selectedTab, setFileVariableValuesForTab],
  );

  const { uploadButtonIdPrefix, selectDivIdPrefix } = useMemo(() => {
    const id = _.uniqueId();
    return {
      uploadButtonIdPrefix: `upload-button-${id}-`,
      selectDivIdPrefix: `select-files-div-${id}-`,
    };
  }, []);

  // Since we are custom styling the file upload button, by styling the label
  // and hiding the input, we are losing the keyboard accessibility of the button.
  // To get around this, I am listening to keyboard events for 'enter' and 'space'
  // and seeing if they are on this button, if they are trigger to corresponding
  // 'Select files' input
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (
        // Enter            // Space
        (e.key === 'Enter' || e.key === ' ') &&
        document.activeElement?.id.includes(selectDivIdPrefix)
      ) {
        const index = document.activeElement.id.split('-').at(-1);
        document.getElementById(`${uploadButtonIdPrefix}${index}`)?.click();
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [selectDivIdPrefix, uploadButtonIdPrefix]);

  const operationModel = useEditorModel(operationEditor);
  const [defaultVariableKey, setDefaultVariableKey] = useState<string>();
  useEffect(() => {
    const disposable = operationModel?.onDidChangeContent(() => {
      const context = getCurrentDocsContext(
        operationModel,
        'first-non-fragment',
        [],
        schema,
      );

      setDefaultVariableKey(
        context.variables
          .filter((variable) => variable.usageCount > 0)
          .find(
            (variable) =>
              variable.name.includes('file') || context.variables.length === 1,
          )?.name,
      );
    });
    return () => disposable?.dispose();
  }, [operationModel, schema]);

  return (
    <div className={classnames(className, 'flex flex-col overflow-hidden')}>
      <PublicVariantWarning
        className="mx-6 my-2"
        settingsEndpointUrl={settingsEndpointUrl}
        settingsSubscriptionUrl={settingsSubscriptionUrl}
        explorerBehavioralFlags={explorerBehavioralFlags}
      />
      <div className="relative flex-1 overflow-hidden">
        <Badge className="absolute right-2 top-0 z-20">JSON</Badge>
        <StudioEditorContainer
          editor={variablesEditor}
          schemaInfo={schemaInfo}
          className="h-full"
        />
      </div>
      <div className="mt-auto">
        {currentFileVariableValues?.map((fileVariable, index) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="mb-4 ml-4 mr-2 flex items-center justify-between gap-2"
          >
            <FormControl className="flex flex-row items-center">
              <FormLabel>Key</FormLabel>
              <Input
                type="text"
                placeholder={defaultVariableKey ?? 'variable key'}
                className="min-w-14 max-w-64"
                value={fileVariable.variableKey}
                onChange={(e) => {
                  handleFileVariablesNameChange(
                    e.target.value,
                    fileVariable,
                    index,
                    currentFileVariableValues,
                  );
                }}
              />
            </FormControl>
            {fileVariable.files && fileVariable.files.length > 0 && (
              <>
                <div className="truncate text-secondary">
                  {`${fileVariable.files?.length} ${format.pluralize(
                    fileVariable.files?.length,
                    'file',
                  )} (${format.bytes(
                    [...fileVariable.files].reduce(
                      (acc, file) => acc + file.size,
                      0,
                    ),
                  )})`}
                </div>
                <Tooltip
                  label={[...fileVariable.files].map(({ name }) => (
                    <div key={name}>{name}</div>
                  ))}
                >
                  <IconInfo className="flex-none" />
                </Tooltip>
              </>
            )}
            <FormControl
              className="flex flex-row items-center"
              isRequired
              id={`${uploadButtonIdPrefix}${index}`}
            >
              <FormLabel
                id={`${selectDivIdPrefix}${index}`}
                role="button"
                aria-controls="filename"
                tabIndex={0} // Select files is like a button, but we don't want to capture the
                // on click in this button, we want to capture it in the input
                className="cursor-pointer truncate rounded px-3 py-1 text-link hover:bg-btn-secondary-hover hover:text-link focus-visible:bg-btn-secondary-hover aria-disabled:text-placeholder aria-disabled:hover:text-placeholder"
              >
                Select file(s)
              </FormLabel>
              <Input
                className="hidden"
                type="file"
                multiple={fileVariable.isMultiFile}
                onChange={(event) => {
                  onSelectFiles(
                    event,
                    fileVariable,
                    index,
                    currentFileVariableValues,
                  );
                }}
              />
              <Tooltip
                label={
                  fileVariable.isMultiFile
                    ? 'Set protocol to single file'
                    : 'Set protocol to multi file'
                }
              >
                <span className="mx-2">
                  <Switch
                    showTextualState={false}
                    isSelected={fileVariable.isMultiFile}
                    onChange={(value) =>
                      handleFileVariablesMultiFileSwitchChange(
                        value,
                        fileVariable,
                        index,
                        currentFileVariableValues,
                      )
                    }
                    aria-describedby={
                      fileVariable.isMultiFile
                        ? 'Set protocol to single file'
                        : 'Set protocol to multi file'
                    }
                  />
                </span>
              </Tooltip>

              <IconButton
                aria-label="Delete this variable"
                variant="hidden"
                icon={<IconTrash />}
                onClick={() => handleRemoveFileVariable(index)}
              />
            </FormControl>
          </div>
        ))}
        {explorerBehavioralFlags.showFileUpload && (
          <Button
            className="mb-2 ml-2"
            variant="hidden"
            leftIcon={<IconPlus />}
            onClick={handleAddFileClick}
          >
            Add files
          </Button>
        )}
      </div>
    </div>
  );
};
